import React, { useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import { CloudUpload, Send, CheckCircle, Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface FederationMembershipFormProps {
  registration: {
    id: string;
    club_name: string;
    codice?: string;
    fivl: boolean;
    adesione_nr?: number;
    adesione_file_url?: string;
    pagamento_file_url?: string;
  };
  onSuccess: () => void;
}

interface FormData {
  adesione_nr: string;
  adesione_file: File | null;
  pagamento_file: File | null;
}

const FederationMembershipForm: React.FC<FederationMembershipFormProps> = ({
  registration,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    adesione_nr: registration.adesione_nr?.toString() || '',
    adesione_file: null,
    pagamento_file: null,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (fieldName: 'adesione_file' | 'pagamento_file') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({ ...prev, [fieldName]: e.target.files![0] }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!formData.adesione_nr) {
      setError(t('pilot.federation.errors.membershipNumberRequired'));
      return;
    }

    const numValue = parseInt(formData.adesione_nr, 10);
    if (isNaN(numValue)) {
      setError(t('pilot.federation.errors.invalidNumber'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First update the federation membership number
      await api.put(`/cards/${registration.id}/federation-membership`, {
        adesione_nr: numValue,
      });

      // Then upload files if provided
      if (formData.adesione_file) {
        const adesioneData = new FormData();
        adesioneData.append('file', formData.adesione_file);
        await api.post(
          `/cards/${registration.id}/upload-document?document_type=adesione`,
          adesioneData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }

      if (formData.pagamento_file) {
        const pagamentoData = new FormData();
        pagamentoData.append('file', formData.pagamento_file);
        await api.post(
          `/cards/${registration.id}/upload-document?document_type=pagamento`,
          pagamentoData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }

      setSuccess(true);
      onSuccess();
    } catch (err: any) {
      console.error('Submit error:', err);
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError(t('pilot.federation.errors.submitFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const hasExistingData = registration.fivl || registration.adesione_nr;

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'primary.main' }}>
          {t('pilot.federation.title')} - {registration.club_name}
        </Typography>
        {hasExistingData && (
          <Chip
            icon={<CheckCircle />}
            label={t('pilot.federation.alreadySubmitted')}
            color="success"
            size="small"
          />
        )}
      </Box>

      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('pilot.federation.success')}
        </Alert>
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {hasExistingData && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('pilot.federation.existingDataInfo')}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                required
                type="number"
                label={t('pilot.federation.membershipNumber')}
                name="adesione_nr"
                value={formData.adesione_nr}
                onChange={handleInputChange}
                helperText={t('pilot.federation.membershipNumberHelper')}
                sx={{ mb: 2 }}
              />

              {registration.codice && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('pilot.federation.yourCode')}:
                  </Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
                    {registration.codice}
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              {t('pilot.federation.documents')}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {t('pilot.federation.membershipForm')}
                  {registration.adesione_file_url && (
                    <Chip
                      label={t('pilot.federation.uploaded')}
                      color="success"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUpload />}
                  sx={{
                    height: '48px',
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                  }}
                >
                  {formData.adesione_file
                    ? formData.adesione_file.name
                    : registration.adesione_file_url
                    ? t('pilot.federation.replaceFile')
                    : t('pilot.federation.selectFile')}
                  <input
                    hidden
                    accept=".jpg,.jpeg,.png,.pdf,.gif,.webp"
                    type="file"
                    onChange={handleFileChange('adesione_file')}
                  />
                </Button>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  {t('pilot.federation.paymentReceipt')}
                  {registration.pagamento_file_url && (
                    <Chip
                      label={t('pilot.federation.uploaded')}
                      color="success"
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUpload />}
                  sx={{
                    height: '48px',
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                  }}
                >
                  {formData.pagamento_file
                    ? formData.pagamento_file.name
                    : registration.pagamento_file_url
                    ? t('pilot.federation.replaceFile')
                    : t('pilot.federation.selectFile')}
                  <input
                    hidden
                    accept=".jpg,.jpeg,.png,.pdf,.gif,.webp"
                    type="file"
                    onChange={handleFileChange('pagamento_file')}
                  />
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                sx={{
                  background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #5a67d8 30%, #68356b 90%)',
                  },
                }}
              >
                {loading ? t('pilot.federation.submitting') : t('pilot.federation.submit')}
              </Button>
            </Box>
          </form>
        </>
      )}
    </Paper>
  );
};

export default FederationMembershipForm;
